import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import relative from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary/index.tsx'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react'
import {
    createRoutesFromChildren,
    matchRoutes,
    useNavigationType,
    useLocation,
} from 'react-router-dom'
import { useEffect } from 'react'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(relative)
dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(isSameOrAfter)
dayjs.extend(isToday)
dayjs.extend(isYesterday)
dayjs.extend(weekOfYear)
dayjs.extend(isSameOrBefore)

import '@/configs/i18n'
import '@/modules/InternalRating/locales/register'
import 'vnetwork-ui/dist/vnetwork-ui.min.css'
import './index.css'

import APP_PACKAGE from '../package.json'

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 300000,
        },
        mutations: {
            onError: (error) => {
                console.error(error)
            },
        },
    },
})

// Initialize Sentry with environment-specific configuration
if (
    import.meta.env.APP_SENTRY_DSN &&
    (import.meta.env.PROD || import.meta.env.APP_ENV === 'staging')
) {
    const release = `${APP_PACKAGE.name}-${APP_PACKAGE.version}.${import.meta.env.APP_ENV}`

    console.log(`Initializing Sentry for ${release}`)

    Sentry.init({
        dsn: import.meta.env.APP_SENTRY_DSN,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration(),
        ],
        environment: import.meta.env.APP_ENV,
        release: release,
        // Performance Monitoring
        tracesSampleRate: import.meta.env.APP_ENV === 'development' ? 0.1 : 1.0,
        // Session Replay
        replaysSessionSampleRate:
            import.meta.env.APP_ENV === 'production' ? 1.0 : 0.1,
        replaysOnErrorSampleRate: 1.0,
        enabled:
            import.meta.env.APP_ENV === 'production' ||
            import.meta.env.APP_ENV === 'staging',
    })
}

const container = document.getElementById('root')!
const root = createRoot(container)

root.render(
    <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
            <App />
            <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
            />
            <Toaster position="top-center" containerStyle={{ zIndex: 99999 }} />
        </QueryClientProvider>
    </ErrorBoundary>
)
